import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IDropdownProps {
  label?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_event: SelectChangeEvent<unknown>) => void;
  value: string;
  required?: boolean;
  name?: string;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
  isTranslationProvided?: boolean;
  disabled?: boolean;
}

const StyledSelect = styled(Select)(({ theme, error }) => ({
  height: '48px',
  borderRadius: '10px',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  '& .MuiSelect-select': {
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    '&:focus': {
      borderRadius: '5px',
    },
  },
  '& fieldset': {
    padding: '12px',
    color: theme.palette.primary.main,
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&:hover fieldset': {
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&.Mui-focused fieldset': {
    border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '& .MuiOutlinedInput-input.Mui-disabled': {
    '-webkit-text-fill-color': theme.palette.primary.main,
    opacity: 0.3,
  },
}));

function Dropdown({
  label,
  options,
  onChange,
  value,
  required,
  name,
  onBlur,
  error,
  placeholder,
  isTranslationProvided,
  disabled,
}: IDropdownProps) {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" justifyContent="center">
      {label && (
        <Typography variant="meta" color="primary">
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      )}
      <FormControl fullWidth>
        <StyledSelect
          labelId="dropdown-label"
          id="simple-select-dropdown"
          value={value}
          name={name}
          onChange={onChange}
          inputProps={{ 'aria-label': 'Without label' }}
          displayEmpty
          onBlur={onBlur}
          error={!!error}
          disabled={disabled}
        >
          <MenuItem value="" disabled>
            <Typography variant="bodyRegular" style={{ color: '#80808080' }}>
              {placeholder || t('activity.assignActivity.pleaseSelect')}
            </Typography>
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Typography variant="bodyRegular" color="primary.main">
                {isTranslationProvided ? t(option.label) : option.label}
              </Typography>
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      {error && (
        <FormHelperText sx={{ color: 'red', marginTop: '-2px' }}>{t(error)}</FormHelperText>
      )}
    </Box>
  );
}

export default Dropdown;
